import { AI_LEVELS, type Color, type Variant, getVariant } from '@chessbut/engine';
import { type ColorChoice, QUICK_TIME_CONTROL, type TimeControl } from '@chessbut/shared';
import { Bot, Link2, Users, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { LocalGame, saveKeyFor } from '../game/localGame.ts';
import { api } from '../lib/api.ts';
import { timeControlLabel } from '../lib/format.ts';
import { navigate } from '../lib/router.tsx';
import { load, save } from '../lib/storage.ts';
import { track } from '../lib/track.ts';
import { client, useOnline } from '../online/client.ts';
import { Modal } from './Modal.tsx';

/**
 * Live numbers so people can see the queue isn't empty: players online right now, and how many
 * are waiting for a game of this variant (you included, if you're searching).
 */
export function LiveCount({ variant }: { variant?: string }) {
  const { stats, status } = useOnline();
  if (!stats || status !== 'online') return null;
  const inQueue = variant ? (stats.searching[variant] ?? 0) : Object.values(stats.searching).reduce((a, b) => a + b, 0);
  const name = variant ? getVariant(variant)?.info.name : null;
  return (
    <p className="live-count" aria-live="polite">
      <span className="live-dot" aria-hidden />
      <span>
        <b className="num">{stats.online}</b> online
      </span>
      <span aria-hidden>·</span>
      <span>
        <b className="num">{stats.playing}</b> in games
      </span>
      <span aria-hidden>·</span>
      <span>
        <b className="num">{inQueue}</b> in the {name ? `${name} ` : ''}queue
      </span>
    </p>
  );
}

export function PlayButtons({ variant, featured }: { variant: Variant; featured: boolean }) {
  const online = useOnline();
  const [modal, setModal] = useState<'bot' | 'invite' | null>(null);
  const searching = online.stats?.searching[variant.id] ?? 0;
  const inQueue = online.queue?.variant === variant.id;
  const passAndPlay = () => {
    const key = saveKeyFor({ variant: variant.id, mode: 'local' });
    const existing = LocalGame.restore(key);
    if (!existing || existing.status().over) {
      LocalGame.clear(key);
      new LocalGame({ variant: variant.id, mode: 'local', human: 0, level: 1 });
    }
    track('local.start', { variant: variant.id });
    navigate(`/${variant.id}/local`);
  };
  // Hidden pieces can't be played online yet (online games share every move), so these are
  // computer and pass & play only.
  if (variant.hiddenInfo) {
    return (
      <>
        <button className="btn primary big block" onClick={() => setModal('bot')}>
          <Bot className="icon" />
          vs Computer
          <span className="sub">online play coming later</span>
        </button>
        <button className="btn block" onClick={passAndPlay}>
          <Users className="icon" />
          Pass &amp; play on this device
        </button>
        {modal === 'bot' && <BotSetup variant={variant} onClose={() => setModal(null)} />}
      </>
    );
  }
  return (
    <>
      <button
        className="btn primary big block"
        onClick={() => {
          if (inQueue) client.cancelQuickMatch();
          else {
            client.quickMatch(variant.id);
            track('queue.join', { variant: variant.id });
          }
        }}
      >
        <Zap className="icon" />
        {inQueue ? 'Searching…' : 'Quick match'}
        <span className="sub">
          {inQueue ? 'tap to cancel' : `rated · ${timeControlLabel(QUICK_TIME_CONTROL)}${featured ? ' · 2× pts' : ''}${searching > 0 ? ` · ${searching} in queue now` : ''}`}
        </span>
      </button>
      <LiveCount variant={variant.id} />
      <div className="two-up">
        <button className="btn small" onClick={() => setModal('bot')}>
          <Bot className="icon" />
          vs Computer
        </button>
        <button className="btn small" onClick={() => setModal('invite')}>
          <Link2 className="icon" />
          Invite a friend
        </button>
      </div>
      <button className="btn ghost block quiet" onClick={passAndPlay}>
        <Users className="icon" />
        Pass &amp; play on this device
      </button>
      {modal === 'bot' && <BotSetup variant={variant} onClose={() => setModal(null)} />}
      {modal === 'invite' && <InviteSetup variant={variant} onClose={() => setModal(null)} />}
    </>
  );
}

function BotSetup({ variant, onClose }: { variant: Variant; onClose(): void }) {
  const key = saveKeyFor({ variant: variant.id, mode: 'bot' });
  const existing = LocalGame.restore(key);
  const resumable = existing && !existing.status().over && existing.game.ply > 0;
  const [level, setLevel] = useState<number>(load('bot-level', 2));
  const [color, setColor] = useState<'white' | 'black' | 'random'>(load('bot-color', 'white'));
  const names = variant.info.bots;
  const [ratings, setRatings] = useState<Record<number, number>>({});
  useEffect(() => {
    let live = true;
    api<{ bots: { level: number; rating: number }[] }>(`/api/bots/${variant.id}`)
      .then((r) => live && setRatings(Object.fromEntries(r.bots.map((b) => [b.level, b.rating]))))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [variant.id]);

  const start = () => {
    save('bot-level', level);
    save('bot-color', color);
    const human: Color = color === 'white' ? 0 : color === 'black' ? 1 : Math.random() < 0.5 ? 0 : 1;
    LocalGame.clear(key);
    new LocalGame({ variant: variant.id, mode: 'bot', human, level });
    track('bot.start', { variant: variant.id, level });
    navigate(`/${variant.id}/bot`);
  };

  return (
    <Modal title="Play the computer" onClose={onClose}>
      {resumable && (
        <button className="btn block resume" onClick={() => navigate(`/${variant.id}/bot`)}>
          Resume your game <span className="sub">{existing.game.ply} moves in · level {existing.config.level}</span>
        </button>
      )}
      <label>Difficulty</label>
      <div className="levels">
        {AI_LEVELS.map((l, i) => (
          <button key={l.id} className={`level ${level === l.id ? 'on' : ''}`} onClick={() => setLevel(l.id)}>
            <span className="level-dots">
              {AI_LEVELS.map((_, j) => (
                <i key={j} className={j <= i ? 'filled' : ''} />
              ))}
            </span>
            <span className="level-name">
              {names?.[i] ?? l.name}
              {ratings[l.id] ? <span className="level-rating num">{ratings[l.id]}</span> : null}
            </span>
            <span className="level-blurb">{l.blurb}</span>
          </button>
        ))}
      </div>
      <label>Play as</label>
      <div className="tabs">
        {(['white', 'random', 'black'] as const).map((c) => (
          <button key={c} aria-selected={color === c} onClick={() => setColor(c)}>
            {c === 'random' ? 'Random' : c === 'white' ? 'White' : 'Black'}
          </button>
        ))}
      </div>
      <button className="btn primary big block" onClick={start}>
        Start game
      </button>
      <p className="hint-text center">Practice games are unrated. When nobody else is around, Quick match offers a rated game against the computer nearest your rating.</p>
    </Modal>
  );
}

const CLOCKS: TimeControl[] = [
  { initial: 180, increment: 2 },
  { initial: 300, increment: 3 },
  { initial: 600, increment: 0 },
  { initial: 900, increment: 10 },
  { initial: 0, increment: 0 },
];

function InviteSetup({ variant, onClose }: { variant: Variant; onClose(): void }) {
  const [tc, setTc] = useState<TimeControl>(load('invite-tc', CLOCKS[2]));
  const [color, setColor] = useState<ColorChoice>('random');
  return (
    <Modal title="Invite a friend" onClose={onClose}>
      <p className="muted sheet-lead">You get a link to send. The game starts as soon as your friend opens it. Invite games are unrated.</p>
      <label>Clock</label>
      <div className="chips">
        {CLOCKS.map((c) => (
          <button key={timeControlLabel(c)} className={`chip ${c.initial === tc.initial && c.increment === tc.increment ? 'is-selected' : ''}`} onClick={() => setTc(c)}>
            {timeControlLabel(c)}
          </button>
        ))}
      </div>
      <label>You play</label>
      <div className="tabs">
        {(['white', 'random', 'black'] as const).map((c) => (
          <button key={c} aria-selected={color === c} onClick={() => setColor(c)}>
            {c === 'random' ? 'Random' : c === 'white' ? 'White' : 'Black'}
          </button>
        ))}
      </div>
      <button
        className="btn primary big block"
        onClick={() => {
          save('invite-tc', tc);
          client.createRoom(variant.id, tc, color);
          onClose();
        }}
      >
        Create invite link
      </button>
    </Modal>
  );
}
